import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Toolbar, 
  IconButton, 
  Box, 
  Menu, 
  MenuItem, 
  Typography, 
  Divider, 
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { 
  AccountCircle as AccountCircleIcon, 
  SwitchAccount as SwitchAccountIcon,
  Brightness4 as Brightness4Icon,
  Brightness7 as Brightness7Icon,
  Settings as SettingsIcon,
  Cached as CachedIcon
} from '@mui/icons-material';
import { NavLink, useNavigate } from 'react-router-dom';
import logo from '../assets/inklantern_animated_logo_black.gif';
import { useDevAuth } from '../utils/useDevAuth';
import { useQueryClient } from 'react-query';
import { useParams, useLocation } from 'react-router-dom';
import { useDashboardType } from '../hooks/useBrands';
import { useAxiosClient } from '../utils/useAxiosClient';
import SettingsModal from './SettingsModal';

interface Organization {
  id: number;
  name: string;
  timezone: string;
}

// Map IANA timezone to short display label
const getTimezoneLabel = (tz: string): string => {
  const map: Record<string, string> = {
    'America/New_York': 'ET',
    'America/Chicago': 'CT',
    'America/Denver': 'MT',
    'America/Los_Angeles': 'PT',
    'America/Anchorage': 'AKT',
    'Pacific/Honolulu': 'HT',
    'UTC': 'UTC',
  };
  return map[tz] || tz;
};

interface AppHeaderProps {
  isDarkMode: boolean;
  onToggleTheme: () => void;
}

const AppHeader: React.FC<AppHeaderProps> = ({ isDarkMode, onToggleTheme }) => {
  const { user, logout } = useDevAuth();
  // Only allow access to color settings for crant.ai users
  const canAccessColorSettings = user?.email?.toLowerCase().endsWith('@crant.ai') || user?.email?.toLowerCase() === 'weezilla@gmail.com';
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [orgAnchorEl, setOrgAnchorEl] = useState<null | HTMLElement>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const open = Boolean(anchorEl);
  const orgMenuOpen = Boolean(orgAnchorEl);
  const axiosClient = useAxiosClient();
  const navigate = useNavigate();
  const { orgId: currentOrgId } = useParams<{ orgId: string }>();
  const location = useLocation();
  const queryClient = useQueryClient();
  
  const orgIdNum = currentOrgId ? parseInt(currentOrgId, 10) : 0;
  const { dashboardType, loading: dashboardTypeLoading, error: dashboardTypeError } = useDashboardType(orgIdNum);
  
  const activeDashboardType = dashboardType;

  useEffect(() => {
    if (organizations.length > 0 && currentOrgId) {
      const matchedOrg = organizations.find(org => String(org.id) === currentOrgId);
      if (matchedOrg) {
        setCurrentOrg(matchedOrg);
      }
    }
  }, [organizations, currentOrgId]);

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleOrgSwitchClick = async (event: React.MouseEvent<HTMLElement>) => {
    setOrgAnchorEl(event.currentTarget);
    
    if (organizations.length === 0) {
      setLoadingOrgs(true);
      try {
        const response = await axiosClient.get('/api/available-orgs');
        setOrganizations(response.data.organizations);
      } catch (error) {
        console.error('Error fetching organizations:', error);
      } finally {
        setLoadingOrgs(false);
      }
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOrgClose = () => {
    setOrgAnchorEl(null);
  };

  const handleOrgSwitch = async (org: Organization) => {
    setCurrentOrg(org);
    
    // Navigate to the root of the target organization
    const targetPath = `/org/${org.id}`;
    console.log(`Switching org: Navigating to root path: ${targetPath}`);
    navigate(targetPath);
    
    // Clear session storage for this organization to force fresh data fetch
    Object.keys(sessionStorage).forEach(key => {
      if (key.includes(`inklantern_org_${org.id}`)) {
        sessionStorage.removeItem(key);
      }
    });
    
    // We don't need the try/catch or API call here anymore
    // The OrgRedirector component mapped to targetPath will handle the rest.
    
    handleOrgClose(); // Close the menu
  };

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
    handleClose();
  };

  const handleClearCache = () => {
    queryClient.clear();
    setSnackbarOpen(true);
  };


  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleSettingsClick = () => {
    setSettingsOpen(true);
  };

  const handleSettingsClose = () => {
    setSettingsOpen(false);
  };

  const handleColorsUpdated = () => {
    // Only invalidate brand-related queries instead of clearing everything
    queryClient.invalidateQueries(['brand-love']);
    queryClient.invalidateQueries(['brand-tracker']);
    queryClient.invalidateQueries(['dei-index']);
    
    // Dispatch custom event for immediate component refresh
    window.dispatchEvent(new CustomEvent('brandColorsUpdated'));
    
    console.log('Brand colors updated - invalidating brand-related queries and notifying components');
  };

  const getNavLinks = () => {
    if (dashboardTypeLoading) return [];
    if (dashboardTypeError) {
      console.error("Error loading dashboard type for nav links:", dashboardTypeError);
      return [];
    }
    
    if (activeDashboardType === 'personal_branding') {
      return [
        { name: 'General Metrics', path: `/org/${currentOrgId}/employee-brand/general` },
        { name: 'Personal Metrics', path: `/org/${currentOrgId}/employee-brand/personal` },
      ];
    } else {
      return [
        { name: 'Brand Tracker', path: `/org/${currentOrgId}/brand-tracker` },
        { name: 'Pillar Breakdown', path: `/org/${currentOrgId}/pillar-breakdown` },
        { name: 'Keyword Analysis', path: `/org/${currentOrgId}/keyword-analysis` },
        { name: 'Image Comparison', path: `/org/${currentOrgId}/image-comparison` },
      ];
    }
  };

  const navLinks = getNavLinks();

  const iconButtonSx = {
    color: 'rgba(255, 255, 255, 0.72)',
    '&:hover': { color: '#fff', backgroundColor: 'rgba(255, 255, 255, 0.08)' },
    '&:focus-visible': { outline: '2px solid rgba(255, 255, 255, 0.6)', outlineOffset: 1 },
  };

  return (
    <AppBar position="static" elevation={0}>
      <Toolbar sx={{ minHeight: { xs: 56, sm: 60 }, px: { xs: 2, md: 3 }, columnGap: { xs: 1, md: 3 }, rowGap: 0, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        <Box sx={{ height: 34, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {/* The logo GIF has a black background; "lighten" lets the header
              color show through it so it sits on the ink bar without a box. */}
          <img
            src={logo}
            alt="Inklantern"
            style={{ height: '100%', width: 'auto', objectFit: 'contain', mixBlendMode: 'lighten' }}
          />
        </Box>

        <Box
          component="nav"
          aria-label="Dashboard pages"
          sx={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'stretch',
            alignSelf: 'stretch',
            gap: { xs: 0, md: 0.5 },
            minWidth: 0,
            // On phones the tabs drop to their own scrollable row under the logo and icons
            order: { xs: 3, md: 0 },
            flexBasis: { xs: '100%', md: 'auto' },
            height: { xs: 44, md: 'auto' },
            mx: { xs: -1, md: 0 },
            overflowX: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {!dashboardTypeLoading && navLinks.map((link) => (
            <Box
              key={link.name}
              component={NavLink}
              to={link.path}
              sx={(theme) => ({
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                px: { xs: 1.25, md: 1.75 },
                whiteSpace: 'nowrap',
                textDecoration: 'none',
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'rgba(255, 255, 255, 0.62)',
                transition: 'color 120ms ease',
                '&:hover': { color: '#fff' },
                '&:focus-visible': { outline: '2px solid rgba(255, 255, 255, 0.6)', outlineOffset: -4, borderRadius: 1 },
                // The lantern: a warm underline that marks the current page.
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  left: 12,
                  right: 12,
                  bottom: 0,
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                  backgroundColor: 'transparent',
                },
                '&.active': { color: '#fff' },
                '&.active::after': {
                  backgroundColor: theme.ink.lantern,
                  boxShadow: `0 -2px 14px ${theme.ink.lantern}`,
                },
              })}
            >
              {link.name}
            </Box>
          ))}
          {dashboardTypeLoading && <CircularProgress color="inherit" size={20} sx={{ ml: 2, alignSelf: 'center' }}/>}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0, ml: { xs: 'auto', md: 0 } }}>
          <Tooltip title="Clear data cache">
            <IconButton onClick={handleClearCache} sx={iconButtonSx} aria-label="Clear data cache">
              <CachedIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
            <IconButton onClick={onToggleTheme} sx={iconButtonSx} aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
              {isDarkMode ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
            </IconButton>
          </Tooltip>
          {canAccessColorSettings && (
            <Tooltip title="Theme & colors">
              <IconButton onClick={handleSettingsClick} sx={iconButtonSx} aria-label="Theme and colors">
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Switch organization">
            <IconButton
              onClick={handleOrgSwitchClick}
              aria-label="Switch organization"
              aria-controls={orgMenuOpen ? 'org-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={orgMenuOpen ? 'true' : undefined}
              sx={iconButtonSx}
            >
              <SwitchAccountIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Account">
            <IconButton
              onClick={handleProfileClick}
              aria-label="Account"
              aria-controls={open ? 'profile-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              sx={iconButtonSx}
            >
              <AccountCircleIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Toolbar>
      
      <Menu
        id="org-menu"
        anchorEl={orgAnchorEl}
        open={orgMenuOpen}
        onClose={handleOrgClose}
        MenuListProps={{
          'aria-labelledby': 'org-switch-button',
        }}
        PaperProps={{ sx: { maxHeight: '70vh', minWidth: 240 } }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem disabled>
          <Typography variant="body2" color="textSecondary">
            Switch organization
          </Typography>
        </MenuItem>
        <Divider />
        {loadingOrgs ? (
          <MenuItem disabled sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
            <CircularProgress size={24} />
          </MenuItem>
        ) : organizations.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2">No organizations available</Typography>
          </MenuItem>
        ) : (
          organizations.map((org) => (
            <MenuItem 
              key={org.id} 
              onClick={() => handleOrgSwitch(org)}
              selected={currentOrg?.id === org.id}
            >
              {org.name}
            </MenuItem>
          ))
        )}
      </Menu>

      <Menu
        id="profile-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'profile-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleClose} disabled>
          <Typography variant="body2" color="textSecondary">
            {user?.email}
          </Typography>
        </MenuItem>
        {currentOrg && (
          <MenuItem disabled>
            <Typography variant="body2" color="textSecondary">
              Org Timezone: {getTimezoneLabel(currentOrg.timezone)}
            </Typography>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={handleLogout}>Logout</MenuItem>
      </Menu>
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity="success" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          Cache cleared. Refreshing data.
        </Alert>
      </Snackbar>
      
      <SettingsModal 
        open={settingsOpen} 
        onClose={handleSettingsClose} 
        isDarkMode={isDarkMode} 
        onColorsUpdated={handleColorsUpdated}
      />
    </AppBar>
  );
};

export default AppHeader; 